import React from "react";
import { Send, AlertCircle, Loader2, Upload, Copy } from "lucide-react";
import WhatsAppPreview from "../WhatsAppPreview";
import { useMessage } from "../../contexts/MessageContext";

const SingleMessage = () => {
  const {
    MESSAGE_TYPES,
    LANGUAGES,
    messageForm,
    sending,
    sendingUpload,
    sendResult,
    selectedLanguage,
    messageType,
    mediaFile,
    mediaUrl,
    caption,
    variables,
    variableValues,
    captionVariables,
    captionVariableValues,
    previewMessage,
    previewCaption,
    handlePhoneNumberChange,
    handleMessageChange,
    handleCaptionChange,
    handleMessageTypeChange,
    handleFileChange,
    handleUpload,
    handleSendMessage,
    copyToClipboard,
    setSelectedLanguage,
    getCodeExample,
    handleVariableChange,
    handleCaptionVariableChange,
    isLoggedIn,
  } = useMessage();

  const hasVariables = variables.length > 0;
  const hasCaptionVariables = captionVariables.length > 0;

  return (
    <div className="space-y-8 text-gray-900">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Send Message</h3>
          <p className="text-sm text-gray-600 mb-6">
            Send WhatsApp messages instantly with real-time preview. Complete
            API documentation and code examples available below.
          </p>

          {!isLoggedIn && (
            <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm flex items-center">
              <AlertCircle size={16} className="text-blue-600 mr-2" />
              <span className="text-blue-700">
                Connect WhatsApp first to send messages
              </span>
            </div>
          )}

          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Phone Number
            </label>
            <input
              type="text"
              placeholder="9876543210"
              value={messageForm.phoneNumber}
              onChange={handlePhoneNumberChange}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:border-[#01CC54] focus:ring-[#01CC54]/40"
              disabled={!isLoggedIn || sending}
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter 10 digit Indian mobile number
            </p>
          </div>

          {/* Message Type */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Message Type
            </label>
            <select
              className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 focus:border-[#01CC54] focus:ring-[#01CC54]/40"
              value={messageType}
              onChange={handleMessageTypeChange}
              disabled={!isLoggedIn || sending || sendingUpload}
            >
              {MESSAGE_TYPES.map((t) => (
                <option key={t.value} value={t.value} className="text-gray-900">
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {/* Message Textarea */}
          {messageType === "text" && (
            <div>
              <label className="block text-sm font-medium text-gray-600 m-2">
                Message
              </label>
              <textarea
                placeholder="Enter your message. Use *bold*, _italic_, ~strikethrough~. Use {{variable}} for dynamic values."
                value={messageForm.message}
                onChange={handleMessageChange}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:border-[#01CC54] focus:ring-[#01CC54]/40 resize-none"
                rows={4}
                disabled={!isLoggedIn || sending}
              />
              <p className="text-xs text-gray-500 mt-1">
                Use WhatsApp formatting: *bold*, _italic_, ~strikethrough~, and
                variables like {"{{name}}"}.
              </p>
            </div>
          )}

          {/* Variable Inputs for Message */}
          {hasVariables && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h4 className="font-semibold mb-2 text-gray-700">
                Fill in Variables
              </h4>
              {variables.map((v) => (
                <div key={v} className="mb-3">
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    {v}
                  </label>
                  <input
                    type="text"
                    placeholder={`Enter value for ${v}`}
                    value={variableValues[v] || ""}
                    onChange={(e) => handleVariableChange(v, e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:border-[#01CC54] focus:ring-[#01CC54]/40"
                    disabled={!isLoggedIn || sending}
                  />
                </div>
              ))}
            </div>
          )}

              {/* Upload UI */}
              {(messageType === "image" ||
                messageType === "video" ||
                messageType === "document") && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">
                      {messageType === "image"
                        ? "Upload Image"
                        : messageType === "video"
                        ? "Upload Video"
                        : "Upload PDF Document"}
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        accept={
                          messageType === "image"
                            ? "image/*"
                            : messageType === "video"
                            ? "video/*"
                            : ".pdf,application/pdf"
                        }
                        onChange={handleFileChange}
                        disabled={!isLoggedIn || sending || sendingUpload}
                        className="flex-1"
                      />
                      <button
                        className="flex items-center bg-[#048AAD] px-3 py-2 rounded-lg hover:bg-[#048AAD]/90 text-sm disabled:bg-gray-100 disabled:text-gray-400 text-white"
                        type="button"
                        disabled={
                          !mediaFile || sending || sendingUpload || !isLoggedIn
                        }
                        onClick={handleUpload}
                      >
                        {sendingUpload ? (
                          <Loader2 size={14} className="animate-spin mr-1" />
                        ) : (
                          <Upload size={14} className="mr-1" />
                        )}
                        Upload
                      </button>
                    </div>
                    {mediaFile && (
                      <span className="text-xs mt-1 block text-gray-500">
                        Selected: {mediaFile.name}
                      </span>
                    )}
                    {mediaUrl && (
                      <span className="text-xs block text-[#01CC54]">
                        File uploaded successfully!
                      </span>
                    )}
                  </div>

                  {/* Caption textarea */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">
                      Caption (Optional)
                    </label>
                    <textarea
                      placeholder="Add a caption with formatting support... Use *bold*, _italic_, ~strikethrough~, etc."
                      value={caption}
                      onChange={handleCaptionChange}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:border-[#01CC54] focus:ring-[#01CC54]/40 resize-none"
                      rows={3}
                      disabled={!isLoggedIn || sending}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Caption supports WhatsApp formatting and variables like{" "}
                      {"{{name}}"}.
                    </p>
                  </div>

                  {/* Variable Inputs for Caption */}
                  {captionVariables.length > 0 && (
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 mt-3">
                      <h4 className="mb-2 font-semibold text-gray-700">
                        Caption Variables
                      </h4>
                      {captionVariables.map((v) => (
                        <div key={v} className="mb-3">
                          <label className="block text-sm font-medium text-gray-600 mb-1">
                            {v}
                          </label>
                          <input
                            type="text"
                            placeholder={`Enter value for ${v}`}
                            value={captionVariableValues[v] || ""}
                            onChange={(e) =>
                              handleCaptionVariableChange(v, e.target.value)
                            }
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:border-[#01CC54] focus:ring-[#01CC54]/40"
                            disabled={!isLoggedIn || sending}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

          {/* Send button */}
          <button
            onClick={handleSendMessage}
            disabled={
              sending ||
              sendingUpload ||
              !isLoggedIn ||
              (messageType !== "text" && !mediaUrl)
            }
            className="w-full flex items-center justify-center px-4 py-3 mt-4 bg-[#048AAD] text-white rounded-lg hover:bg-[#048AAD]/90 disabled:bg-gray-100 disabled:text-gray-400 transition-colors"
          >
            {sending ? (
              <Loader2 size={18} className="mr-2 animate-spin" />
            ) : (
              <>
                <Send size={18} className="mr-2" />
                Send Message
              </>
            )}
          </button>

          {sendResult && (
            <div
              className={`p-3 mt-2 rounded-lg text-sm ${
                sendResult.success
                  ? "bg-green-50 text-green-700 border border-green-200"
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}
            >
              {sendResult.message}
            </div>
          )}
        </div>

        {/* RIGHT: WhatsApp Preview */}
        <div>
          <WhatsAppPreview
            messageType={messageType}
            message={previewMessage}
            caption={previewCaption}
            mediaUrl={mediaUrl}
            phoneNumber={messageForm.phoneNumber}
            mediaFile={mediaFile}
          />
        </div>
      </div>

      {/* BOTTOM: API DOCUMENTATION */}
      <div className="border-t border-gray-200 pt-8">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">API Documentation</h3>
            <button
              onClick={() => copyToClipboard(getCodeExample, "Code")}
              className="flex items-center bg-[#048AAD] text-white px-3 py-2 rounded-lg hover:bg-[#048AAD]/90 text-sm"
            >
              <Copy size={14} className="mr-1" />
              Copy Code
            </button>
          </div>

          <div className="bg-gray-50 text-gray-900 p-3 rounded-lg text-sm font-mono overflow-x-auto border border-gray-200">
            POST {process.env.REACT_APP_API_URL}/api/whatsapp/send-message
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Select Programming Language:
            </label>
            <div className="flex flex-wrap gap-2 mb-4">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang}
                  onClick={() => setSelectedLanguage(lang)}
                  className={`px-3 py-1 rounded-md text-sm transition-colors ${
                    selectedLanguage === lang
                      ? "bg-[#048AAD] text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {lang}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 text-gray-900 p-4 rounded-lg text-sm font-mono overflow-x-auto max-h-96 overflow-y-auto border border-gray-200">
            <pre className="whitespace-pre-wrap">{getCodeExample}</pre>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleMessage;
